import React, { useEffect, useMemo, useRef, useState } from "react";
import MapPropertyPopupCard from "components/Card/MapPropertyPopupCard";
import RemoveWarningDialog from "components/Dialog/RemoveWarningDialog";
import useProperties from "hooks/useProperties";
import MapPropertyPopupCardSkeleton from "components/Skeleton/MapPropertyPopupCardSkeleton";
import { useMediaQuery } from "react-responsive";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import MapboxBaseMap from "./MapboxBaseMap";
import { Marker, Popup, Source, Layer } from "react-map-gl/mapbox";

const SearchPropertiesMap = () => {
  const {
    mapProperties,
    loading,
    propertyDetails,
    getPropertyDetails,
    activeMapProperty,
    activatePropertyOnMap,
  } = useProperties();
  const isMobile = useMediaQuery({ query: "(max-width: 640px)" });
  const mapRef = useRef(null);
  const [popupInfo, setPopupInfo] = useState(null);
  const [isLoadingPopup, setIsLoadingPopup] = useState(false);

  const minusHeight = isMobile ? "228px" : "164px";

  // Prepare cluster source when map properties change
  const clusterSource = useMemo(() => {
    if (!mapProperties?.results?.length) {
      return {
        type: "FeatureCollection",
        features: [],
      };
    }

    return {
      type: "FeatureCollection",
      features: mapProperties.results
        .filter((property) => property?.coordinates?.coordinates?.length === 2)
        .map((property) => ({
          type: "Feature",
          properties: {
            id: property._id,
            propertyData: property,
            isActive:
              property._id === (popupInfo?.propertyId || activeMapProperty),
          },
          geometry: {
            type: "Point",
            coordinates: [
              property.coordinates.coordinates[0],
              property.coordinates.coordinates[1],
            ],
          },
        })),
    };
  }, [mapProperties, popupInfo, activeMapProperty]);

  // Cluster layer style
  const clusterLayer = {
    id: "clusters",
    type: "circle",
    source: "properties",
    filter: ["has", "point_count"],
    paint: {
      "circle-color": "#1DA1F2",
      "circle-radius": [
        "step",
        ["get", "point_count"],
        35, // Default size
        10,
        60, // Step 1: 10+ points, radius 60
        25,
        70, // Step 2: 25+ points, radius 70
        50,
        80, // Step 3: 50+ points, radius 80
      ],
      "circle-stroke-width": 2,
      "circle-stroke-color": "#fff",
      "z-index": 1000,
      "circle-opacity": 0.3,
    },
  };

  // Cluster count layer style
  const clusterCountLayer = {
    id: "cluster-count",
    type: "symbol",
    source: "properties",
    filter: ["has", "point_count"],
    layout: {
      "text-field": "{point_count_abbreviated}",
      "font-weight": "bold",
      "text-font": ["DIN Offc Pro Bold", "Arial Unicode MS Bold"],
      "text-size": [
        "step",
        ["get", "point_count"],
        30, // Default text size
        10,
        40, // Step 1: 10+ points, text size 40
        25,
        50, // Step 2: 25+ points, text size 50
        50,
        60, // Step 3: 50+ points, text size 60
      ],
    },
    paint: {
      "text-color": "#ffffff",
    },
  };

  // Unclustered point layer style - with conditional active styling
  const unclusteredPointLayer = {
    id: "unclustered-point",
    type: "circle",
    source: "properties",
    filter: ["!", ["has", "point_count"]],
    paint: {
      "circle-color": [
        "case",
        ["get", "isActive"],
        "#ff5722", // Active color - orange
        "#1DA1F2", // Default color - blue
      ],
      "circle-radius": [
        "case",
        ["get", "isActive"],
        20, // Active size
        18, // Default size
      ],
      "circle-stroke-width": 2,
      "circle-stroke-color": "#fff",
    },
  };

  // Handle click on a marker or cluster
  const onClick = (event) => {
    if (!event.features) return;

    const feature = event.features[0];

    // Handle cluster click
    if (feature && feature.properties.cluster) {
      const clusterId = feature.properties.cluster_id;
      const mapboxSource = mapRef.current.getSource("properties");

      mapboxSource.getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;

        mapRef.current.flyTo({
          center: feature.geometry.coordinates,
          zoom,
          speed: 1,
        });
      });
      return;
    }

    // Handle marker click
    if (feature && !feature.properties.cluster) {
      const coordinates = feature.geometry.coordinates.slice();
      const id = feature.properties.id;

      setIsLoadingPopup(true);
      setPopupInfo({
        longitude: coordinates[0],
        latitude: coordinates[1],
        propertyId: id,
        loading: true,
      });

      // Get property details
      getPropertyDetails(id).then((details) => {
        setIsLoadingPopup(false);
        setPopupInfo((prev) => ({
          ...prev,
          details: details,
          loading: false,
        }));
      });
    }
  };

  // When activeMapProperty changes, show the popup for that property
  useEffect(() => {
    if (!activeMapProperty || !mapProperties?.results) return;

    const property = mapProperties.results.find(
      (p) => p._id === activeMapProperty
    );
    if (!property || !property.coordinates?.coordinates) return;

    const coordinates = property.coordinates.coordinates;

    // Store coordinates in state even when popup is closed
    if (mapRef.current) {
      mapRef.current.flyTo({
        center: [coordinates[0], coordinates[1]],
        zoom: 15,
        duration: 1000,
      });
    }

    // Set popup info
    setIsLoadingPopup(true);
    setPopupInfo({
      longitude: coordinates[0],
      latitude: coordinates[1],
      propertyId: property._id,
      loading: true,
    });

    // Get property details
    getPropertyDetails(property._id).then((details) => {
      setIsLoadingPopup(false);
      setPopupInfo((prev) => ({
        ...prev,
        details: details,
        loading: false,
      }));
    });
  }, [activeMapProperty, mapProperties]);

  // Handle map popup close
  const handlePopupClose = () => {
    // Only set propertyId to null but keep the coordinates
    if (popupInfo) {
      setPopupInfo((prev) => ({
        ...prev,
        propertyId: null,
      }));
    } else {
      setPopupInfo(null);
    }

    if (activeMapProperty) {
      activatePropertyOnMap(null);
    }
  };

  // Handle map click outside of markers or clusters
  const handleMapClick = (event) => {
    // If we have features, handle them normally with the onClick handler
    if (event.features && event.features.length > 0) {
      onClick(event);
    } else {
      // If no features, close the popup
      handlePopupClose();
    }
  };

  // Only render popup when propertyId exists
  const shouldShowPopup = popupInfo && popupInfo.propertyId;

  return (
    <>
      {loading ? (
        <div
          style={{ height: `calc(100vh - ${minusHeight})` }}
          className={`w-full flex-1 block z-10`}
        >
          <Skeleton height="100%" width="100%" borderRadius={0} />
        </div>
      ) : (
        <MapboxBaseMap
          interactiveLayerIds={["clusters", "unclustered-point"]}
          mapRef={mapRef}
          mapStyle={{
            height: `calc(100vh - ${minusHeight})`,
            flex: 1,
            borderRadius: 0,
            zIndex: 1,
          }}
          zoomLevel={11}
          zoomPosition="top-right"
          className="property-map"
          onClick={handleMapClick}
        >
          {/* Map cluster and point layers */}
          <Source
            id="properties"
            type="geojson"
            data={clusterSource}
            cluster={true}
            clusterMaxZoom={14}
            clusterRadius={50}
          >
            <Layer {...clusterLayer} />
            <Layer {...clusterCountLayer} />
            <Layer {...unclusteredPointLayer} />
          </Source>

          {/* Popup for selected property */}
          {shouldShowPopup && (
            <Popup
              longitude={popupInfo.longitude}
              latitude={popupInfo.latitude}
              anchor="bottom"
              onClose={handlePopupClose}
              closeOnClick={false}
              className="property-map-popup"
              closeButton={false}
              style={{
                maxWidth: "246px",
              }}
            >
              {isLoadingPopup || popupInfo.loading ? (
                <MapPropertyPopupCardSkeleton />
              ) : (
                <MapPropertyPopupCard
                  property={{
                    ...popupInfo.details,
                    average_gross_yield: mapProperties?.averageGrossYield,
                  }}
                />
              )}
            </Popup>
          )}
        </MapboxBaseMap>
      )}
    </>
  );
};

export default SearchPropertiesMap;
